import { useMemo } from 'react';

import { clientConfig } from '@/config/clientConfig';
import {
  AddItemGqlInput,
  ChangeItemGqlInput,
  RemoveItemGqlInput,
  useCollectionAddItemMutation,
  useCollectionChangeItemMutation,
  useCollectionRemoveItemMutation,
} from '@/shared/generated/graphql';
import { CollectionItem } from '@/shared/lib/secure-json/core/secure-json-collection/types';
import { secureJsonEncryptItem } from '@/shared/lib/secure-json/utils';
import { CollectionName } from '@/shared/lib/sj-orm/constants';

type AddItemData = {
  collectionName: CollectionName;
  item: CollectionItem;
};

type ChangeItemData = {
  collectionName: CollectionName;
  item: CollectionItem;
};

type RemoveItemData = {
  collectionName: CollectionName;
  item: CollectionItem;
};

export const useCollectionsApi = () => {
  const [addItemMutation] = useCollectionAddItemMutation();
  const [changeItemMutation] = useCollectionChangeItemMutation();
  const [removeItemMutation] = useCollectionRemoveItemMutation();

  return useMemo(() => {
    const addItem = async (props: AddItemData) => {
      const data: AddItemGqlInput = {
        collectionName: props.collectionName,
        dtoId: props.item.decryptedData.id,
        item: secureJsonEncryptItem(props.item),
        platform: clientConfig.api.collectionPlatformKey,
      };

      return await addItemMutation({ variables: { data } });
    };

    const changeItem = async (props: ChangeItemData) => {
      const data: ChangeItemGqlInput = {
        collectionName: props.collectionName,
        dtoId: props.item.decryptedData.id,
        item: secureJsonEncryptItem(props.item),
        signature: props.item.signature!,
        platform: clientConfig.api.collectionPlatformKey,
      };

      return await changeItemMutation({ variables: { data } });
    };

    const removeItem = async (props: RemoveItemData) => {
      const data: RemoveItemGqlInput = {
        collectionName: props.collectionName,
        dtoId: props.item.decryptedData.id,
        signature: props.item.signature!,
        platform: clientConfig.api.collectionPlatformKey,
      };

      return await removeItemMutation({ variables: { data } });
    };

    return {
      addItem,
      changeItem,
      removeItem,
    };
  }, []);
};
